import { supabase } from "../supabaseClient";
import { logger } from "../utils/logger";
import { getRelationshipDisplayLabel } from "../utils/relationshipLabels";

const CLAIMED_SELECT = `
  id,
  item_text,
  url,
  price,
  thumbnail_url,
  claimed_by,
  claimed_at,
  purchased_at,
  claimed_for_child_name,
  surprise_recipient_label,
  surprise_recipient_name_id,
  surprise_visible_to_group,
  names:name_id ( name )
`;

export async function fetchClaimedItems(userId) {
  const { data, error } = await supabase
    .from("wishlist_items")
    .select(CLAIMED_SELECT)
    .eq("claimed_by", userId)
    .order("claimed_at", { ascending: false });

  if (error) {
    logger.error('claimedItemsService', 'fetch_claimed_items_failed', { userId, error: error.message });
    throw error;
  }
  return data || [];
}

export async function unclaimItem({ itemId, currentClaimantId, userId }) {
  if (currentClaimantId !== userId) {
    throw new Error('Only the claimant can unclaim this item.');
  }

  const { error } = await supabase
    .from("wishlist_items")
    .update({ claimed_by: null, claimed_at: null, purchased_at: null, claimed_for_child_name: null })
    .eq("id", itemId);

  if (error) {
    logger.error('claimedItemsService', 'item_unclaimed_failed', { itemId, userId, error: error.message });
    throw error;
  }
  logger.info('claimedItemsService', 'item_unclaimed', { itemId, userId });
}

export async function togglePurchased({ itemId, purchased }) {
  const { error } = await supabase
    .from("wishlist_items")
    .update({ purchased_at: purchased ? new Date().toISOString() : null })
    .eq("id", itemId);

  if (error) {
    logger.error('claimedItemsService', 'toggle_purchased_failed', { itemId, purchased, error: error.message });
    throw error;
  }
  logger.info('claimedItemsService', purchased ? 'item_purchased' : 'item_unpurchased', { itemId });
}

export async function fetchRecipientOptions(groupId, userId) {
  const [membersRes, childListsRes, ownListsRes] = await Promise.all([
    supabase.from("group_members").select("user_id, users(display_name)").eq("group_id", groupId),
    supabase.from("names").select("id, name, user_id, relationship_label, relationship_custom_label").eq("group_id", groupId).eq("is_child_list", true),
    supabase.from("names").select("id, user_id").eq("group_id", groupId).eq("is_child_list", false),
  ]);

  if (membersRes.error || childListsRes.error || ownListsRes.error) {
    const error = membersRes.error || childListsRes.error || ownListsRes.error;
    logger.error('claimedItemsService', 'fetch_recipient_options_failed', { groupId, userId, error: error.message });
    throw error;
  }

  const memberRows = membersRes.data || [];
  const memberNameById = {};
  memberRows.forEach((m) => {
    memberNameById[m.user_id] = m.users?.display_name || m.user_id;
  });

  // Every member has exactly one personal, non-child list per group — used to attach
  // the names.id a surprise item's `surprise_recipient_name_id` FK should point at.
  const ownNameIdByUserId = {};
  (ownListsRes.data || []).forEach((n) => {
    ownNameIdByUserId[n.user_id] = n.id;
  });

  const memberOptions = memberRows
    .filter((m) => m.user_id !== userId)
    .map((m) => ({
      key: `member:${m.user_id}`,
      label: memberNameById[m.user_id],
      nameId: ownNameIdByUserId[m.user_id] ?? null,
    }));

  const childOptions = (childListsRes.data || []).map((c) => ({
    key: `child:${c.id}`,
    label: `${c.name} (${getRelationshipDisplayLabel(c)} of ${memberNameById[c.user_id] || c.user_id})`,
    nameId: c.id,
  }));

  return [...memberOptions, ...childOptions].sort((a, b) => a.label.localeCompare(b.label));
}

export async function addSurpriseItem({ itemText, url, price, userId, thumbnailUrl, recipientLabel, recipientNameId, visibleToGroup }) {
  const { data, error } = await supabase
    .from("wishlist_items")
    .insert({
      name_id: null,
      item_text: itemText,
      url: url || null,
      price,
      user_id: userId,
      claimed_by: userId,
      claimed_at: new Date().toISOString(),
      thumbnail_url: thumbnailUrl,
      surprise_recipient_label: recipientLabel,
      surprise_recipient_name_id: recipientNameId ?? null,
      surprise_visible_to_group: visibleToGroup,
    })
    .select(CLAIMED_SELECT)
    .single();

  if (error) {
    logger.error('claimedItemsService', 'surprise_item_add_failed', { itemText, recipientLabel, error: error.message });
    throw error;
  }
  logger.info('claimedItemsService', 'surprise_item_added', { itemId: data.id, recipientLabel, recipientNameId: recipientNameId ?? null, visibleToGroup, hasThumbnail: !!thumbnailUrl });
  return data;
}

export async function deleteSurpriseItem({ id, recipientNameId }) {
  const { error } = await supabase.from("wishlist_items").delete().eq("id", id);

  if (error) {
    logger.error('claimedItemsService', 'surprise_item_delete_failed', { itemId: id, error: error.message });
    throw error;
  }
  logger.info('claimedItemsService', 'surprise_item_deleted', { itemId: id, recipientNameId: recipientNameId ?? null });
}

export function subscribeToClaimedUpdates(userId, onChange) {
  const channel = supabase
    .channel(`claimed-${userId}`)
    .on("broadcast", { event: "claimed-updated" }, onChange)
    .subscribe();

  return () => supabase.removeChannel(channel);
}

export function notifyClaimedUpdated(userId) {
  const topic = `claimed-${userId}`;

  // Mirrors wishlistItemsService.notifyItemsUpdated: supabase.channel() reuses an existing
  // channel object for a topic that's already joined (e.g. this tab's own
  // subscribeToClaimedUpdates listener), so subscribe()-then-send would silently no-op.
  const existing = supabase.getChannels().find((c) => c.topic === `realtime:${topic}`);
  if (existing && existing.state === "joined") {
    existing.send({ type: "broadcast", event: "claimed-updated" });
    return;
  }

  const channel = supabase.channel(topic);
  channel.subscribe((status) => {
    if (status === "SUBSCRIBED") {
      channel.send({ type: "broadcast", event: "claimed-updated" });
      supabase.removeChannel(channel);
    }
  });
}
